import {
  ApiListResponse,
  InvoiceReceiptDTO,
  StaffUserRole,
  UserStaffBean,
} from "@gymflow/types";
import axios, { AxiosInstance } from "axios";

import { createSortParam } from "../createSortParam";
import { trimFields } from "./features/trimFiels";

export type UserStaffApiType = ReturnType<typeof userStaffApi>;

const userStaffApi = (axiosInstance: AxiosInstance, clubId: number) => {
  const clubPrefix = `club/${clubId}/`;
  const fieldsToTrim = [
    "email",
    "firstName",
    "lastName",
    "mobileNumber",
    "username",
  ];

  return {
    create(newResource: any) {
      return axiosInstance.post(
        `${clubPrefix}user/staff`,
        trimFields(newResource, fieldsToTrim),
      );
    },
    update(id: string, patchedFields: Partial<any>) {
      return axiosInstance.patch(
        `${clubPrefix}user/staff/${id}`,
        trimFields(patchedFields, fieldsToTrim),
      );
    },
    find({
      page = 0,
      limit = 10,
      extraParams = {},
      sort,
    }: {
      readonly extraParams?: {
        readonly activeUser?: boolean;
        readonly firstName?: string;
        readonly appointableList?: string;
        readonly id?: string[];
        readonly [k: string]: any;
      };
      readonly page?: number;
      readonly limit?: number;
      readonly sort?: {
        readonly field: string;
        readonly desc: boolean;
      };
    }) {
      const config = {
        params: {
          page,
          size: limit,
          ...extraParams,
        },
      } as any;
      if (sort) {
        config.params.sort = createSortParam(sort);
      }
      return axiosInstance.get<ApiListResponse<UserStaffBean>>(
        `${clubPrefix}user/staff`,
        config,
      );
    },
    findById(id: string) {
      return axiosInstance.get<UserStaffBean>(`${clubPrefix}user/staff/${id}`);
    },

    async updatePicture(
      id: string,
      {
        blob,
        name,
      }: {
        blob: any;
        name: string;
      },
    ) {
      const blobResponse = await axios.get(blob, {
        responseType: "blob",
      });
      const formData = new FormData();
      formData.append("file", blobResponse.data, name);
      return axiosInstance.post(
        `${clubPrefix}user/staff/${id}/picture`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
    },
    activate(id: string) {
      return axiosInstance.post(
        `${clubPrefix}user/staff/${id}/activate-staff-user`,
      );
    },
    deactivate(id: string) {
      return axiosInstance.post(
        `${clubPrefix}user/staff/${id}/deactivate-staff-user`,
      );
    },
    setClubAccessible(id: string, accessibleClubs: number[]) {
      return axiosInstance.patch(
        `${clubPrefix}user/staff/set-club-accessible/${id}`,
        {
          clubAccessibleIdList: accessibleClubs,
        },
      );
    },
    setRole(id: string, role: StaffUserRole) {
      return axiosInstance.patch<UserStaffBean>(
        `${clubPrefix}user/staff/set-role/${id}`,
        {
          role,
        },
      );
    },
    delete(staffId: string) {
      return axiosInstance.delete(`${clubPrefix}user/staff/${staffId}`);
    },
    getInvoiceReceipt(invoiceNumber: string) {
      return axiosInstance.get<InvoiceReceiptDTO>(
        `${clubPrefix}invoice/${invoiceNumber}/receipt`,
      );
    },
  };
};

export default userStaffApi;
